/**
 * Webview Events Helper Functions
 *
 * Provides type-safe helper functions for sending and receiving
 * webview events throughout the application.
 */

import { useMemo } from 'react';

import { useWebviewBridge } from './webview-bridge-context';
import {
  type BridgeReadyPayload,
  type ChangeThemeColorPayload,
  type LogoutPayload,
  type RequestThemeColorPayload,
  type SetInitialThemePayload,
  type SetPullToRefreshEnabledPayload,
  WebviewReceiveEventType,
  WebviewSendEventType,
} from './webview-events.type';

/**
 * Typed hook for sending/receiving webview events.
 *
 * @example
 * ```tsx
 * const { sendPullToRefreshEnabled } = useWebviewEvents();
 * sendPullToRefreshEnabled(true);
 * ```
 */
export const useWebviewEvents = () => {
  const { send, on } = useWebviewBridge();

  return useMemo(
    () => ({
      /**
       * Tell native whether the pull-to-refresh gesture should be enabled.
       *
       * @param enabled - `true` when the main scrollable container is at the top,
       *                  `false` when the user has scrolled down in a main container
       */
      sendPullToRefreshEnabled: async (enabled: boolean): Promise<void> => {
        await send<SetPullToRefreshEnabledPayload>(WebviewSendEventType.SET_PULL_TO_REFRESH_ENABLED, {
          enabled,
        });
      },
      /**
       * Tell native that the user has logged out so it can clear its own session
       * and run any post-logout flow.
       *
       * @param payload - Optional `{ reason }` used as an analytics-only label
       *                  (e.g. `'User initiated'`, `'Auth error'`).
       */
      sendLogout: async (payload: LogoutPayload = {}): Promise<void> => {
        await send<LogoutPayload>(WebviewSendEventType.LOGOUT, payload);
      },
      /**
       * Tell native which color to paint its system chrome (status bar, nav bar)
       * so it matches the current web theme. Native side decides exactly which
       * surfaces to repaint; the web only contributes the hex.
       *
       * @param payload - `{ color }` as uppercase hex without `#` (e.g. `'242426'`).
       */
      sendChangeThemeColor: async (payload: ChangeThemeColorPayload): Promise<void> => {
        await send<ChangeThemeColorPayload>(WebviewSendEventType.CHANGE_THEME_COLOR, payload);
      },
      /**
       * Tell native the bridge is initialized and listening. Fire-and-forget,
       * sent once on init; native must not post messages before receiving it.
       */
      sendBridgeReady: async (): Promise<void> => {
        await send<BridgeReadyPayload>(WebviewSendEventType.BRIDGE_READY, {});
      },
      /**
       * Ask native to resolve the launch theme (stored manual choice, else the
       * device OS theme). Native replies with SET_INITIAL_THEME.
       */
      sendRequestThemeColor: async (): Promise<void> => {
        await send<RequestThemeColorPayload>(WebviewSendEventType.REQUEST_THEME_COLOR, {});
      },
      /**
       * Listen for the launch theme pushed by native in reply to REQUEST_THEME_COLOR.
       *
       * @returns Unsubscribe function
       */
      onSetInitialTheme: (handler: (payload: SetInitialThemePayload) => void): (() => void) =>
        on<SetInitialThemePayload>(WebviewReceiveEventType.SET_INITIAL_THEME, (message) => {
          handler(message.data);
        }),
    }),
    [send, on]
  );
};
